// Top bar: breadcrumbs, global search (Ctrl+K), notifications, theme toggle, user menu.
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, Sun, Moon, LogOut, Menu, User, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { useTheme } from "@/theme/ThemeProvider";
import { useAuth } from "@/auth/AuthContext";
import { api } from "@/api/client";
import { Sidebar } from "./Sidebar";
import { fmtDate } from "@/utils/format";
import { NetworkStatus } from "@/components/NetworkStatus";
import { UpdateBadge } from "@/components/UpdateBadge";
import { FxRatesWidget } from "@/components/FxRatesWidget";

export function TopBar() {
  const { theme, toggle } = useTheme();
  const { user, logout } = useAuth();
  const [search, setSearch] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [allNotifsOpen, setAllNotifsOpen] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const kd = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") { e.preventDefault(); setSearch(true); }
    };
    window.addEventListener("keydown", kd);
    return () => window.removeEventListener("keydown", kd);
  }, []);

  useEffect(() => {
    let mounted = true;
    const readDismissed = () => {
      try { return new Set(JSON.parse(localStorage.getItem("hasyapi_dismissed_notifs") || "[]")); }
      catch { return new Set(); }
    };
    const load = async () => {
      try {
        const [rem, cards] = await Promise.all([
          api.get("/reminders?status=pending&upcoming_days=14&limit=50"),
          api.get("/credit-cards").catch(() => ({ data: [] })),
        ]);
        // Kredi kartı ekstre bildirimleri: ekstre kesim tarihi DÜN olan kartlar için bugün göster.
        const y = new Date();
        y.setDate(y.getDate() - 1);
        const yesterdayDay = y.getDate();
        const yesterdayIso = `${y.getFullYear()}-${String(y.getMonth()+1).padStart(2,"0")}-${String(y.getDate()).padStart(2,"0")}`;
        const dismissed = readDismissed();
        const cardNotifs = (cards.data || [])
          .filter((c) => Number(c.statement_day || 0) === yesterdayDay)
          .map((c) => ({
            id: `cc-stmt-${c.id}-${yesterdayIso}`,
            title: `${c.bank_name || "Banka"} ${c.last_four ? c.last_four : (c.name || "")} nolu kredi kartı ekstresi kesilmiştir.`,
            due_at: yesterdayIso,
            priority: "info",
            _synthetic: true,
          }))
          .filter((n) => !dismissed.has(n.id));
        if (mounted) setNotifs([...(rem.data || []), ...cardNotifs]);
      } catch { /* ignore */ }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => { mounted = false; clearInterval(iv); };
  }, []);

  // Sentetik (client-side) bildirimi kalıcı olarak yok say.
  const dismissNotif = (id) => {
    try {
      const prev = JSON.parse(localStorage.getItem("hasyapi_dismissed_notifs") || "[]");
      const arr = Array.isArray(prev) ? prev : [];
      if (!arr.includes(id)) arr.push(id);
      // 90 gün sonra otomatik temizlensin diye çok büyümesin — son 200 ile sınırla.
      localStorage.setItem("hasyapi_dismissed_notifs", JSON.stringify(arr.slice(-200)));
    } catch { /* ignore */ }
    setNotifs((prev) => prev.filter((n) => n.id !== id));
  };

  const unread = notifs.length;

  return (
    <>
      <header className="h-14 border-b border-border bg-card px-3 sm:px-4 flex items-center gap-2 sm:gap-3 sticky top-0 z-30 safe-x" data-testid="topbar">
        <Sheet open={mobileNav} onOpenChange={setMobileNav}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden shrink-0" data-testid="mobile-menu-btn">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72 safe-left">
            <SheetHeader className="sr-only"><SheetTitle>Menu</SheetTitle></SheetHeader>
            <Sidebar onNavigate={() => setMobileNav(false)} />
          </SheetContent>
        </Sheet>

        <div className="flex-1 min-w-0 flex items-center max-w-lg">
          <Button
            variant="outline"
            className="w-full justify-start text-muted-foreground gap-2 h-9 truncate"
            onClick={() => setSearch(true)}
            data-testid="global-search-btn"
          >
            <Search className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline truncate">Ara... (Ctrl+K)</span>
            <span className="sm:hidden">Ara</span>
          </Button>
        </div>

        <div className="flex items-center gap-0.5 sm:gap-1 shrink-0">
          <div className="hidden sm:flex items-center gap-1">
            <FxRatesWidget />
            <NetworkStatus />
            <UpdateBadge />
          </div>
          <DropdownMenu open={notifOpen} onOpenChange={setNotifOpen}>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="relative" data-testid="notification-btn">
                <Bell className="h-5 w-5" />
                {unread > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-500" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80">
              <DropdownMenuLabel>Bildirimler ({unread})</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {notifs.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">Bildirim yok.</div>
              ) : (
                notifs.slice(0, 8).map((n) => (
                  <DropdownMenuItem key={n.id}
                    onSelect={(e) => e.preventDefault()}
                    className="flex-row items-center gap-2 justify-between">
                    <div className="flex-1 min-w-0 cursor-pointer"
                         onClick={() => { nav(n._synthetic ? "/credit-cards" : "/reminders"); setNotifOpen(false); }}>
                      <div className="text-sm font-medium truncate">{n.title}</div>
                      <div className="text-xs text-muted-foreground">{fmtDate(n.due_at)} · {n.priority}</div>
                    </div>
                    {n._synthetic && (
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); dismissNotif(n.id); }}
                        className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-red-600"
                        title="Bildirimi sil"
                        data-testid={`notif-dismiss-${n.id}`}>
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </DropdownMenuItem>
                ))
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setNotifOpen(false); setAllNotifsOpen(true); }}
                                data-testid="notifs-view-all">
                Tümünü Gör ({notifs.length})
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label="Tema değiştir">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="user-menu-btn">
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div>{user?.name}</div>
                <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
                <div className="text-xs text-muted-foreground font-normal capitalize">{user?.role}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" /> Çıkış Yap
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <GlobalSearch open={search} onClose={() => setSearch(false)} />
      <Dialog open={allNotifsOpen} onOpenChange={setAllNotifsOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-hidden p-0 gap-0" data-testid="notifs-all-dialog">
          <div className="px-5 py-4 border-b flex items-center justify-between">
            <div>
              <div className="text-base font-semibold">Tüm Bildirimler</div>
              <div className="text-xs text-muted-foreground">{notifs.length} aktif bildirim</div>
            </div>
          </div>
          <div className="max-h-[65vh] overflow-y-auto">
            {notifs.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">Hiç aktif bildirim yok.</div>
            ) : (
              <ul className="divide-y">
                {notifs.map((n) => (
                  <li key={n.id} className="flex items-start gap-3 px-5 py-3 hover:bg-muted/40">
                    <div className="flex-1 min-w-0 cursor-pointer"
                         onClick={() => { setAllNotifsOpen(false); nav(n._synthetic ? "/credit-cards" : "/reminders"); }}>
                      <div className="text-sm font-medium">{n.title}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {fmtDate(n.due_at)} · {n.priority}
                      </div>
                    </div>
                    {n._synthetic && (
                      <button
                        type="button"
                        onClick={() => dismissNotif(n.id)}
                        className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-red-600"
                        title="Bildirimi sil"
                        data-testid={`notif-dismiss-all-${n.id}`}>
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function GlobalSearch({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ parties: [], invoices: [], cheques: [] });
  const nav = useNavigate();

  useEffect(() => {
    if (!open) { setQ(""); setResults({ parties: [], invoices: [], cheques: [] }); }
  }, [open]);

  useEffect(() => {
    if (!q || q.length < 2) return;
    const c = new AbortController();
    const load = async () => {
      try {
        const [p, i, ch] = await Promise.all([
          api.get(`/parties?q=${encodeURIComponent(q)}&limit=8`),
          api.get(`/invoices?limit=200`),
          api.get(`/cheques?limit=1000&exclude_endorsed=false&exclude_deposited=false`),
        ]);
        const ql = q.toLowerCase();
        setResults({
          parties: p.data.slice(0, 6),
          invoices: i.data.filter(x => (x.invoice_no || "").toLowerCase().includes(ql)).slice(0, 6),
          cheques: ch.data.filter(x => (x.internal_no || "").toLowerCase().includes(ql) || (x.cheque_no || "").toLowerCase().includes(ql)).slice(0, 6),
        });
      } catch { /* ignore */ }
    };
    load();
    return () => c.abort();
  }, [q]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl p-0 gap-0">
        <div className="flex items-center border-b border-border px-3">
          <Search className="h-4 w-4 mr-2 text-muted-foreground" />
          <Input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cari, fatura no, çek no ara..."
            className="border-0 focus-visible:ring-0 h-12"
            data-testid="global-search-input"
          />
        </div>
        <div className="max-h-96 overflow-y-auto p-2 space-y-3">
          {q.length < 2 && <div className="p-4 text-sm text-muted-foreground text-center">En az 2 karakter yazın</div>}
          {results.parties.length > 0 && (
            <Section title="Cari Kartlar">
              {results.parties.map(p => (
                <ResultRow key={p.id} onClick={() => { nav(`/parties/${p.id}`); onClose(); }}
                  primary={p.name} secondary={`${p.code} · ${p.type}`} />
              ))}
            </Section>
          )}
          {results.invoices.length > 0 && (
            <Section title="Faturalar">
              {results.invoices.map(i => (
                <ResultRow key={i.id} onClick={() => { nav(`/invoices/${i.id}`); onClose(); }}
                  primary={i.invoice_no} secondary={`${i.type} · ${i.grand_total}`} />
              ))}
            </Section>
          )}
          {results.cheques.length > 0 && (
            <Section title="Çekler">
              {results.cheques.map(c => {
                // Çekin bulunduğu sayfayı belirle
                const isDeposited = c.deposited === true;
                const isEndorsed = c.endorsed_to;
                const isOwn = c.own === true;
                const isOutgoing = c.direction === "outgoing";
                let target = "/cheques/incoming"; let where = "Alınan";
                if (isOwn) { target = "/cheques/own"; where = "Kendi Çekimiz"; }
                else if (isOutgoing) { target = "/cheques/outgoing"; where = "Verilen"; }
                else if (isDeposited) { target = "/bank-cheques"; where = "Bankaya Verilen"; }
                else if (isEndorsed) { target = "/cheques/endorsed"; where = "Cirolanan"; }
                return (
                  <ResultRow key={c.id} onClick={() => { nav(target); onClose(); }}
                    primary={c.cheque_no || c.internal_no}
                    secondary={`${where} · ${c.status} · ${Number(c.amount||0).toLocaleString("tr-TR")}₺`} />
                );
              })}
            </Section>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
const Section = ({ title, children }) => (
  <div>
    <div className="label-caps px-2 mb-1">{title}</div>
    <div className="space-y-0.5">{children}</div>
  </div>
);
const ResultRow = ({ primary, secondary, onClick }) => (
  <button onClick={onClick} className="w-full text-left px-2 py-1.5 rounded hover:bg-muted flex justify-between items-center text-sm">
    <span className="font-medium">{primary}</span>
    <span className="text-xs text-muted-foreground">{secondary}</span>
  </button>
);
